import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type FoldStep = "closed" | "folded" | "peek" | "open";

export interface BottomSheetFolds {
  /** Height in px for "folded" step. Default: 72 */
  folded?: number;
  /** Height in px for "peek" step. Default: 300 */
  peek?: number;
  /** Max height as fraction of viewport (0-1) for "open" step. Default: 0.85 */
  open?: number;
}

export interface BottomSheetConfig {
  folds?: BottomSheetFolds;
  /** Resistance factor for overdrag (0-1). Default: 0.3 */
  overdragResistance?: number;
  /** Whether the sheet can be fully closed. Default: true */
  dismissible?: boolean;
}

const DEFAULT_FOLDS: Required<BottomSheetFolds> = {
  folded: 72,
  peek: 300,
  open: 0.85,
};

const DEFAULT_CONFIG = {
  overdragResistance: 0.3,
  dismissible: true,
};

/** Minimum drag distance (px) to trigger a step change */
const DRAG_THRESHOLD = 10;
/** Time (ms) to block clicks after drag ends */
const CLICK_PREVENTION_WINDOW = 100;

function getMaxHeight(maxHeightVh: number): number {
  return Math.round(window.innerHeight * maxHeightVh);
}

function applyResistance(height: number, maxHeight: number, resistance: number): number {
  if (height > maxHeight) {
    const overflow = height - maxHeight;
    return maxHeight + overflow * resistance;
  }
  return Math.max(0, height);
}

function mapHeight(step: FoldStep, folds: Required<BottomSheetFolds>): number {
  switch (step) {
    case "closed":
      return 0;
    case "folded":
      return folds.folded;
    case "peek":
      return folds.peek;
    default:
      return getMaxHeight(folds.open);
  }
}

function findScrollableParent(element: HTMLElement, topLevelParent: HTMLElement, offset: number): HTMLElement | null {
  let currentElement: HTMLElement | null = element;

  while (currentElement && currentElement !== topLevelParent) {
    const hasScrollableSpace = currentElement.scrollHeight > currentElement.clientHeight;
    const hasOverflowY = window.getComputedStyle(currentElement).overflowY !== "visible";

    const isScrollToTop = currentElement.scrollTop === 0;
    const isScrollToBottom = currentElement.scrollHeight - currentElement.scrollTop <= currentElement.clientHeight + 1;

    const isFullyScrolled = (offset > 0 && isScrollToTop) || (offset < 0 && isScrollToBottom);

    if (hasScrollableSpace && hasOverflowY && !isFullyScrolled) {
      return currentElement;
    }

    currentElement = currentElement.parentElement;
  }

  return topLevelParent.scrollHeight > topLevelParent.clientHeight ? topLevelParent : null;
}

interface UseBottomSheetOptions {
  defaultStep?: FoldStep;
  onStepChange?: (step: FoldStep) => void;
  config?: BottomSheetConfig;
}

interface UseBottomSheetReturn {
  step: FoldStep;
  setStep: (step: FoldStep) => void;
  dragging: boolean;
  height: number;
  sheetRef: React.RefObject<HTMLDivElement>;
  handlers: {
    onPointerDown: (e: React.PointerEvent) => void;
    onPointerMove: (e: React.PointerEvent) => void;
    onPointerUp: (e: React.PointerEvent) => void;
    onPointerCancel: (e: React.PointerEvent) => void;
  };
}

export function useBottomSheet(options: UseBottomSheetOptions = {}): UseBottomSheetReturn {
  const { defaultStep = "peek", onStepChange, config: userConfig } = options;
  const folds = useMemo(() => ({ ...DEFAULT_FOLDS, ...userConfig?.folds }), [userConfig?.folds]);
  const overdragResistance = userConfig?.overdragResistance ?? DEFAULT_CONFIG.overdragResistance;
  const dismissible = userConfig?.dismissible ?? DEFAULT_CONFIG.dismissible;

  const sheetRef = useRef<HTMLDivElement>(null);
  const [step, setStepInternal] = useState<FoldStep>(defaultStep);
  const [dragging, setDragging] = useState(false);
  const [height, setHeight] = useState<number>(() => mapHeight(defaultStep, folds));

  // Drag state refs (don't need re-renders)
  const dragPreStarted = useRef(false);
  const dragStarted = useRef(false);
  const dragDirectionLocked = useRef<"vertical" | "horizontal" | null>(null);
  const didDrag = useRef(false);
  const didDragTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startY = useRef(0);
  const startX = useRef(0);
  const offsetY = useRef(0);
  const startHeight = useRef<number>(mapHeight(defaultStep, folds));

  const setStep = useCallback(
    (newStep: FoldStep) => {
      setStepInternal(newStep);
      onStepChange?.(newStep);
    },
    [onStepChange],
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (didDragTimeoutRef.current) clearTimeout(didDragTimeoutRef.current);
    };
  }, []);

  // Update height when step changes (not during drag)
  useEffect(() => {
    if (!dragging) {
      setHeight(mapHeight(step, folds));
    }
  }, [step, dragging, folds]);

  // Recalculate open height on resize
  useEffect(() => {
    if (step !== "open" || dragging) return;

    const handleResize = () => setHeight(mapHeight("open", folds));
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [step, dragging, folds]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Store drag start position on the sheet element
      if (sheetRef.current) {
        sheetRef.current.dataset.dragstartY = String(e.clientY);
      }

      // Capture pointer to receive events even when pointer leaves the element
      (e.target as HTMLElement).setPointerCapture(e.pointerId);

      dragPreStarted.current = true;
      dragStarted.current = step !== "open";
      dragDirectionLocked.current = null;
      setDragging(step !== "open");

      startY.current = e.clientY;
      startX.current = e.clientX;
      offsetY.current = 0;
      // Always capture numeric height for dragging
      startHeight.current = sheetRef.current?.clientHeight ?? 0;
    },
    [step],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragPreStarted.current) return;

      offsetY.current = e.clientY - startY.current;
      const offsetX = e.clientX - startX.current;

      // Lock direction on first significant movement
      if (!dragDirectionLocked.current && (Math.abs(offsetY.current) > 5 || Math.abs(offsetX) > 5)) {
        dragDirectionLocked.current = Math.abs(offsetX) > Math.abs(offsetY.current) ? "horizontal" : "vertical";
      }

      // If horizontal scroll detected, bail out and let native scroll handle it
      if (dragDirectionLocked.current === "horizontal") {
        return;
      }

      if (!dragStarted.current && sheetRef.current) {
        const target = findScrollableParent(e.target as HTMLElement, sheetRef.current, offsetY.current);

        if (!target) {
          dragStarted.current = true;
          setDragging(true);
          e.preventDefault();
          // Reset start position so offset begins from here
          startY.current = e.clientY;
          offsetY.current = 0;
          startHeight.current = sheetRef.current?.clientHeight ?? 0;
          return;
        }

        if (offsetY.current > 0 && target.scrollTop === 0) {
          dragStarted.current = true;
          setDragging(true);
          e.preventDefault();
          // Reset start position so offset begins from here
          startY.current = e.clientY;
          offsetY.current = 0;
          startHeight.current = sheetRef.current?.clientHeight ?? 0;
        } else if (offsetY.current < 0 && target.scrollHeight - target.scrollTop <= target.clientHeight + 1) {
          dragStarted.current = true;
          setDragging(true);
          e.preventDefault();
          // Reset start position so offset begins from here
          startY.current = e.clientY;
          offsetY.current = 0;
          startHeight.current = sheetRef.current?.clientHeight ?? 0;
        }
      }

      if (dragStarted.current) {
        e.preventDefault();
        // Calculate new height with rubber-band resistance at boundaries
        const rawHeight = startHeight.current - offsetY.current;
        setHeight(applyResistance(rawHeight, getMaxHeight(folds.open), overdragResistance));
      }
    },
    [folds.open, overdragResistance],
  );

  const resetDragState = useCallback(() => {
    dragPreStarted.current = false;
    dragStarted.current = false;
    dragDirectionLocked.current = null;
    setDragging(false);
    if (sheetRef.current) {
      delete sheetRef.current.dataset.dragstartY;
    }
  }, []);

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      // Release pointer capture if held
      const target = e.target as HTMLElement;
      if (target.hasPointerCapture?.(e.pointerId)) {
        target.releasePointerCapture(e.pointerId);
      }

      // Only process if we have an active drag
      if (!dragPreStarted.current && !dragStarted.current) {
        return;
      }

      const wasDragging = dragStarted.current;
      const finalOffset = offsetY.current;
      resetDragState();

      if (!wasDragging) {
        return;
      }

      // Threshold for detecting intentional drag
      if (Math.abs(finalOffset) < DRAG_THRESHOLD) {
        setHeight(mapHeight(step, folds));
        return;
      }

      // Mark that a real drag occurred to prevent click propagation
      // Reset after a short delay in case no click event fires (common on touch)
      didDrag.current = true;
      if (didDragTimeoutRef.current) clearTimeout(didDragTimeoutRef.current);
      didDragTimeoutRef.current = setTimeout(() => {
        didDrag.current = false;
        didDragTimeoutRef.current = null;
      }, CLICK_PREVENTION_WINDOW);

      const currentHeight = sheetRef.current?.clientHeight ?? 0;

      // Determine new step based on current height and drag direction
      let newStep: FoldStep = step;

      if (currentHeight > folds.peek && finalOffset < 0) {
        newStep = "open";
      } else if (currentHeight > folds.peek && finalOffset > 0) {
        newStep = "peek";
      } else if (currentHeight > folds.folded && finalOffset < 0) {
        newStep = "peek";
      } else if (currentHeight > folds.folded && finalOffset > 0) {
        newStep = "folded";
      } else if (currentHeight > 0 && finalOffset < 0) {
        newStep = "folded";
      } else if (currentHeight > 0 && finalOffset > 0) {
        newStep = dismissible ? "closed" : "folded";
      }

      setStep(newStep);
    },
    [step, setStep, folds, dismissible, resetDragState],
  );

  const handlePointerCancel = useCallback(
    (e: React.PointerEvent) => {
      // Always release pointer capture
      const target = e.target as HTMLElement;
      if (target.hasPointerCapture?.(e.pointerId)) {
        target.releasePointerCapture(e.pointerId);
      }

      if (!dragPreStarted.current && !dragStarted.current) {
        return;
      }
      resetDragState();
      // Snap back to current step height
      setHeight(mapHeight(step, folds));
    },
    [step, folds, resetDragState],
  );

  // Prevent clicks from firing on elements after a drag
  useEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet) return;

    const handleClick = (e: MouseEvent) => {
      if (didDrag.current) {
        e.stopPropagation();
        e.preventDefault();
        didDrag.current = false;
      }
    };

    sheet.addEventListener("click", handleClick, true);
    return () => sheet.removeEventListener("click", handleClick, true);
  }, []);

  // Global safety net: reset drag state if pointer ends outside the sheet
  // Uses refs to avoid stale closures and re-registering listeners
  const stepRef = useRef(step);
  const foldsRef = useRef(folds);
  stepRef.current = step;
  foldsRef.current = folds;

  useEffect(() => {
    const handleGlobalEnd = (e: PointerEvent) => {
      if (!dragPreStarted.current && !dragStarted.current) return;

      // Skip if event target is inside the sheet (local handler will handle it)
      if (sheetRef.current?.contains(e.target as Node)) return;

      resetDragState();
      setHeight(mapHeight(stepRef.current, foldsRef.current));
    };

    window.addEventListener("pointerup", handleGlobalEnd);
    window.addEventListener("pointercancel", handleGlobalEnd);
    return () => {
      window.removeEventListener("pointerup", handleGlobalEnd);
      window.removeEventListener("pointercancel", handleGlobalEnd);
    };
  }, [resetDragState]);

  // Handle touch events for scroll boundary detection
  useEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet) return;

    const handleTouchMove = (e: TouchEvent) => {
      // If horizontal scroll detected, let it through
      if (dragDirectionLocked.current === "horizontal") {
        return;
      }

      // If we're dragging vertically, always prevent scroll
      if (dragStarted.current) {
        e.preventDefault();
        return;
      }

      // Only check boundaries if we've started a potential drag (finger is down)
      if (!dragPreStarted.current) return;

      const touch = e.touches[0];
      const currentOffsetY = touch.clientY - startY.current;
      const target = e.target as HTMLElement;

      // Check if there's a scrollable parent that should handle the scroll
      const scrollableParent = findScrollableParent(target, sheet, currentOffsetY);

      if (!scrollableParent) {
        e.preventDefault();
        return;
      }

      // Prevent scroll if at boundaries (this triggers drag mode)
      if (currentOffsetY > 0 && scrollableParent.scrollTop === 0) {
        e.preventDefault();
      } else if (
        currentOffsetY < 0 &&
        scrollableParent.scrollHeight - scrollableParent.scrollTop <= scrollableParent.clientHeight + 1
      ) {
        e.preventDefault();
      }
    };

    sheet.addEventListener("touchmove", handleTouchMove, { passive: false });
    return () => sheet.removeEventListener("touchmove", handleTouchMove);
  }, []);

  return {
    step,
    setStep,
    dragging,
    height,
    sheetRef,
    handlers: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
      onPointerCancel: handlePointerCancel,
    },
  };
}
